import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams } from 'react-router-dom';

import "./StudioTour.css";
import "../../styles/font.css";

import { TopMenu } from '../../component/TopMenu';
import { checkAuth, handleLogout, getCookie } from '../../utils/authUtils';
import { API_CONFIG } from '../../config/api';

export const StudioTour = () => {
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const [userType, setUserType] = useState(null);
  const [userName, setUserName] = useState("");
  const [tours, setTours] = useState([]);

  const [selectedTypes, setSelectedTypes] = useState([]);
  const [selectedLocations, setSelectedLocations] = useState([]);
  const [selectedInterests, setSelectedInterests] = useState([]);

  const [filteredTours, setFilteredTours] = useState([]);
  const [searchKeyword, setSearchKeyword] = useState('');
  const [searchParams] = useSearchParams();

  const verifyUserToken = async (token) => {
    try {
      const response = await fetch(`${API_CONFIG.MAIN}/verify_user_token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_token: token }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setUserType('user');
        setUserName(data.user_name);
      } else {
        setIsAuthenticated(false);
        setUserType(null);
      }
    } catch (error) {
      console.error('Token verification error:', error);
      setIsAuthenticated(false);
      setUserType(null);
    }
  };

  const verifyArtistToken = async (token) => {
    try {
      const response = await fetch(`${API_CONFIG.MAIN}/verify_artist_token`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ auth_token: token }),
      });

      if (response.ok) {
        const data = await response.json();
        setIsAuthenticated(true);
        setUserType('artist');
        setUserName(data.artist_name);
      } else {
        setIsAuthenticated(false);
        setUserType(null);
      }
    } catch (error) {
      console.error('Token verification error:', error);
      setIsAuthenticated(false);
      setUserType(null);
    }
  };

  useEffect(() => {
    const query = searchParams.get('search');
    if (query) {
      setSearchKeyword(query);
    }
  }, [searchParams]);

  const fetchTours = async () => {
    try {
      const response = await fetch(`${API_CONFIG.MAIN}/get_tour_overview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        }
      });
      
      if (!response.ok) {
        throw new Error('Server Error: get_tour_overview response not ok');
      }
      const data = await response.json();
      if (data.isSuccess) {
        setTours(data.tours);
      }
    } catch (error) {
      console.error('Server Error: get_tour_overview fetch error', error);
    }
  };

  const isVisitableToday = (openDays) => {
    if (!openDays) return false;
    
    // 오늘의 요일 구하기 (0: 일요일, 1: 월요일, ...)
    const today = new Date().getDay();
    
    // tour_open_days가 문자열로 저장되어 있다면 파싱
    const days = typeof openDays === 'string' 
      ? openDays.split(',').map(Number)
      : openDays;
      
    return days.includes(today);
  };

  useEffect(() => {
    checkAuth(getCookie, setIsAuthenticated, setUserType, setUserName);
    fetchTours();
  }, []);

  const gotoMain = () => {
    navigate('/');
  }

  const resetTypeFilter = () => {
    setSelectedTypes([]);
  };

  const resetInterestFilter = () => {
    setSelectedInterests([]);
  };

  const resetLocationFilter = () => {
    setSelectedLocations([]);
  };

  const toggleTypeFilter = (type) => {
    setSelectedTypes(prev => {
      if (prev.includes(type)) {
        return prev.filter(t => t !== type);
      } else {
        return [...prev, type];
      }
    });
  };

  const toggleLocationFilter = (location) => {
    setSelectedLocations(prev => {
      if (prev.includes(location)) {
        return prev.filter(l => l !== location);
      } else {
        return [...prev, location];
      }
    });
  };

  const toggleInterestFilter = (interest) => {
    setSelectedInterests(prev => {
      if (prev.includes(interest)) {
        return prev.filter(i => i !== interest);
      } else {
        return [...prev, interest];
      }
    });
  };

  const applyFilters = () => {
    let filtered = [...tours];
    
    if (searchParams.get('search')) {
      filtered = filtered.filter(tour => 
        tour.tour_name.toLowerCase().includes(searchParams.get('search').toLowerCase()) ||
        tour.tour_location.toLowerCase().includes(searchParams.get('search').toLowerCase()) ||
        (tour.tour_hashtag && tour.tour_hashtag.toLowerCase().includes(searchParams.get('search').toLowerCase()))
      );
    }
    
    if (selectedTypes.length > 0) {
      filtered = filtered.filter(tour => selectedTypes.includes(tour.tour_type));
    }

    if (selectedInterests.length > 0) {
      filtered = filtered.filter(tour => {
        // 관심사 매핑 객체
        const interestMapping = {
          '서양화': tour.interests.westernPainting,
          '한국화': tour.interests.koreanPainting,
          '민화': tour.interests.folkPainting,
          '수채화': tour.interests.waterPainting,
          '판화': tour.interests.printPainting,
          '조각': tour.interests.sculpture,
          '서예': tour.interests.calligraphy,
          '공예': tour.interests.craft,
          '사진': tour.interests.picture,
          '일러스트': tour.interests.illustration
        };
        console.log('interestMapping', interestMapping);
        console.log('selectedInterests', selectedInterests);
    
        // 선택된 관심사 중 하나라도 true인 경우 포함
        return selectedInterests.some(interest => interestMapping[interest]);
      });
    }

    if (selectedLocations.length > 0) {
      filtered = filtered.filter(tour => {
        return selectedLocations.some(location => {
          const locationDo = tour.tour_location_do;
          
          const locationMap = {
            '충청북도': ['충북', '충청북도'],
            '충청남도': ['충남', '충청남도'],
            '전라북도': ['전북', '전라북도'],
            '전라남도': ['전남', '전라남도'],
            '경상북도': ['경북', '경상북도'],
            '경상남도': ['경남', '경상남도'],
            '제주도': ['제주', '제주도']
          };
    
          if (locationMap[location]) {
            return locationMap[location].includes(locationDo);
          }
          
          return location === locationDo;
        });
      });
    }
    
    setFilteredTours(filtered);
  };

  useEffect(() => {
    setFilteredTours(tours);
  }, [tours]);

  useEffect(() => {
    applyFilters();
  }, [selectedTypes, selectedLocations, selectedInterests, tours, searchParams]);

  const handleSearch = () => {
    applyFilters();
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  return (
    <div className="studio-tour">
      <TopMenu 
        isAuthenticated={isAuthenticated}
        userType={userType}
        searchKeyword={searchKeyword}
        setSearchKeyword={setSearchKeyword}
        handleLogout={() => handleLogout(setIsAuthenticated, setUserType, setUserName)}
      />
      <div className="content-body">
        <div className="toolbar-left">
          <div className="toolbar-left__type-menu-area">
            <div className="toolbar-left__type-menu-text">구분</div>
            <div className="toolbar-left__type-reset-btn-block" onClick={resetTypeFilter}>
              <div className="toolbar-left__type-reset-btn-text">초기화</div>
            </div>
          </div>
          <div className="toolbar-left__type-select-area">
            {['아틀리에', '스튜디오', '레지던시', '박물관', '미술관'].map((type) => (
              <div 
                key={type}
                className={`toolbar-left__type-select-btn-block ${selectedTypes.includes(type) ? 'active' : ''}`}
                onClick={() => toggleTypeFilter(type)}
              >
                <div className="toolbar-left__type-select-btn-text">{type}</div>
              </div>
            ))}
          </div>

          <div className="toolbar-left__bar"></div>

          <div className="toolbar-left__type-menu-area">
            <div className="toolbar-left__type-menu-text">분야</div>
            <div className="toolbar-left__type-reset-btn-block" onClick={resetInterestFilter}>
              <div className="toolbar-left__type-reset-btn-text">초기화</div>
            </div>
          </div>

          <div className="toolbar-left__type-select-area">
            {['서양화', '한국화', '민화', '수채화', '판화', '조각', '서예', '공예', '사진', '일러스트'].map((interest) => (
              <div 
                key={interest}
                className={`toolbar-left__type-select-btn-block ${selectedInterests.includes(interest) ? 'active' : ''}`}
                onClick={() => toggleInterestFilter(interest)}
              >
                <div className="toolbar-left__type-select-btn-text">{interest}</div>
              </div>
            ))}

          </div>

          <div className="toolbar-left__bar"></div>

          <div className="toolbar-left__type-menu-area">
            <div className="toolbar-left__type-menu-text">위치</div>
            <div className="toolbar-left__type-reset-btn-block" onClick={resetLocationFilter}>
              <div className="toolbar-left__type-reset-btn-text">초기화</div>
            </div>
          </div>
          <div className="toolbar-left__type-select-area">
            {['서울', '경기', '강원도', '충청북도', '충청남도', '전라북도', '전라남도', '경상북도', '경상남도', '제주도'].map((location) => (
              <div 
                key={location}
                className={`toolbar-left__type-select-btn-block ${selectedLocations.includes(location) ? 'active' : ''}`}
                onClick={() => toggleLocationFilter(location)}
              >
                <div className="toolbar-left__type-select-btn-text">{location}</div>
              </div>
            ))}
          </div>
{/* 
          <div className="toolbar-left__apply-btn-area">
            <div className="toolbar-left__apply-btn-text">필터적용</div>
          </div>  */}
        </div>
        <div className="tour-list-area">
          {filteredTours.map((tour, index) => (
            <div key={index} className="tour-list-area__tour-block">
              <img 
                className="tour-list-area__tour-image"
                onClick={() => navigate(`/studio-detail/${tour.id}`)} 
                src={tour.tour_profile ? `${API_CONFIG.MAIN}/uploads/tour_profiles/${tour.tour_profile}` : "icons/tour_image_sample.png"} 
                alt={tour.tour_name}
              />
              <div className="tour-list-area__tour-info-block">
                {isVisitableToday(tour.tour_open_days) && (
                <div className="tour-list-area__tour-info-available-text">
                  당일 방문 가능
                </div>
                )}
                <div className="tour-list-area__tour-info-title">{tour.tour_name}</div>
                <div className="tour-list-area__tour-info-location-area">
                  <img className="tour-list-area__tour-info-location-icon" src="icons/icon_location.svg" />
                  <div className="tour-list-area__tour-info-location-text">
                    {tour.tour_location} · {tour.tour_type}
                  </div>
                </div>
              </div>
              <div className="tour-list-area__tour-price-text">
                {tour.tour_price}원
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
      
  );
};
